<template>
  <div class="workbench-dashboard" v-loading="loading">
    <div class="dashboard-head">
      <div>
        <div class="dashboard-title">工作台</div>
        <div class="dashboard-subtitle">
          统计范围：{{ workbench.range.label || "近30天" }} · 更新时间：{{
            workbench.today.time || "-"
          }}
        </div>
      </div>
      <div class="dashboard-actions">
        <el-radio-group
          v-model="search.time_range"
          class="time-range-tabs"
          size="small"
          @change="onRangeChange"
        >
          <el-radio-button
            v-for="item in timeRangeOptions"
            :key="item.value"
            :label="item.value"
            >{{ item.label }}</el-radio-button
          >
        </el-radio-group>
        <el-date-picker
          v-if="search.time_range === 'custom'"
          v-model="search.custom_time"
          type="daterange"
          value-format="timestamp"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          size="small"
          class="custom-range-picker"
          @change="getData"
        ></el-date-picker>
        <el-button size="small" type="primary" @click="getData">刷新</el-button>
      </div>
    </div>

    <div class="metric-grid">
      <div
        v-for="card in cards"
        :key="card.key"
        :class="['metric-card', 'metric-card--' + card.key]"
      >
        <div class="metric-card__glow"></div>
        <div class="metric-card__top">
          <span class="metric-card__icon">
            <i :class="metricIcon(card.key)"></i>
          </span>
          <span class="metric-card__label">{{ card.label }}</span>
        </div>
        <div class="metric-card__value">
          {{ formatNumber(card.value) }}<small>{{ card.unit }}</small>
        </div>
        <div class="metric-card__bottom">
          <span
            :class="['trend-badge', 'trend-badge--' + trendDirection(card)]"
          >
            {{ trendText(card) }}
          </span>
        </div>
        <svg class="sparkline" viewBox="0 0 100 34" preserveAspectRatio="none">
          <polygon
            :points="sparklineAreaPoints(card.sparkline)"
            class="sparkline__area"
          ></polygon>
          <polyline
            :points="sparklineLinePoints(card.sparkline)"
            class="sparkline__line"
          ></polyline>
        </svg>
      </div>
    </div>

    <el-row :gutter="16" class="dashboard-row">
      <el-col :lg="16" :md="24" :sm="24" :xs="24">
        <el-card shadow="never" class="chart-panel chart-panel--large">
          <template #header>
            <div class="panel-title">
              <span>线索趋势</span>
              <small
                >{{ workbench.range.label || "当前范围" }} · 线索总数 /
                已转发</small
              >
            </div>
          </template>
          <div
            ref="clueTrendChart"
            class="chart-canvas chart-canvas--large"
          ></div>
        </el-card>
      </el-col>
      <el-col :lg="8" :md="24" :sm="24" :xs="24">
        <el-card shadow="never" class="chart-panel chart-panel--large">
          <template #header>
            <div class="panel-title">
              <span>线索状态分布</span>
              <small>{{ workbench.range.label || "当前范围" }}</small>
            </div>
          </template>
          <div
            ref="clueStatusChart"
            class="chart-canvas chart-canvas--large"
          ></div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="dashboard-row">
      <el-col :lg="12" :md="24" :sm="24" :xs="24">
        <el-card shadow="never" class="chart-panel">
          <template #header>
            <div class="panel-title">
              <span>站点线索 TOP 10</span>
              <small>{{ workbench.range.label || "当前范围" }}</small>
            </div>
          </template>
          <div ref="siteTopChart" class="chart-canvas"></div>
        </el-card>
      </el-col>
      <el-col :lg="12" :md="24" :sm="24" :xs="24">
        <el-card shadow="never" class="chart-panel">
          <template #header>
            <div class="panel-title">
              <span>SEO达标概览</span>
              <small>承诺关键词 / 达标关键词</small>
            </div>
          </template>
          <div class="seo-panel">
            <div ref="seoGaugeChart" class="seo-panel__gauge"></div>
            <div class="seo-panel__stats">
              <div>
                <span>SEO站点</span>
                <strong>{{ formatNumber(workbench.seo.site_count) }}</strong>
              </div>
              <div>
                <span>达标站点</span>
                <strong>{{
                  formatNumber(workbench.seo.qualified_site_count)
                }}</strong>
              </div>
              <div>
                <span>承诺关键词</span>
                <strong>{{
                  formatNumber(workbench.seo.committed_keyword_count)
                }}</strong>
              </div>
              <div>
                <span>达标关键词</span>
                <strong>{{
                  formatNumber(workbench.seo.qualified_keyword_count)
                }}</strong>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="dashboard-row">
      <el-col :lg="16" :md="24" :sm="24" :xs="24">
        <el-card shadow="never" class="chart-panel">
          <template #header>
            <div class="panel-title">
              <span>Google数据趋势</span>
              <small>GA4访问 / GSC点击</small>
            </div>
          </template>
          <div
            ref="googleTrendChart"
            class="chart-canvas chart-canvas--small"
          ></div>
        </el-card>
      </el-col>
      <el-col :lg="8" :md="24" :sm="24" :xs="24">
        <el-card shadow="never" class="health-panel">
          <template #header>
            <div class="panel-title">
              <span>同步健康状态</span>
              <small>本地任务状态</small>
            </div>
          </template>
          <div class="health-list">
            <div
              v-for="item in workbench.sync_health"
              :key="item.label"
              class="health-item"
            >
              <span
                :class="['health-dot', 'health-dot--' + item.status]"
              ></span>
              <div>
                <strong>{{ item.label }}</strong>
                <small>{{ item.value }}</small>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import * as echarts from "echarts/core";
import {
  GraphicComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
} from "echarts/components";
import { BarChart, GaugeChart, LineChart, PieChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";
import { apiWorkbench } from "@/api/workbench";

echarts.use([
  GraphicComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  BarChart,
  GaugeChart,
  LineChart,
  PieChart,
  CanvasRenderer,
]);

const timeRangeOptions = [
  { label: "今日", value: "today" },
  { label: "近7天", value: "7d" },
  { label: "近30天", value: "30d" },
  { label: "近半年", value: "half_year" },
  { label: "近一年", value: "1y" },
  { label: "自定义", value: "custom" },
];

export default {
  data() {
    return {
      loading: false,
      charts: {},
      timeRangeOptions,
      search: {
        time_range: "30d",
        custom_time: [],
      },
      workbench: {
        today: {},
        range: {},
        cards: [],
        clue_trend: [],
        clue_status: [],
        site_top: [],
        seo: {},
        google: { trend: [] },
        sync_health: [],
      },
    };
  },
  computed: {
    cards() {
      return this.workbench.cards || [];
    },
  },
  created() {
    this.getData();
  },
  mounted() {
    window.addEventListener("resize", this.resizeCharts);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeCharts);
    Object.keys(this.charts).forEach((key) => {
      this.charts[key].dispose();
    });
    this.charts = {};
  },
  methods: {
    onRangeChange() {
      if (this.search.time_range !== "custom") {
        this.search.custom_time = [];
        this.getData();
      }
    },
    getData() {
      this.loading = true;
      apiWorkbench(this.buildRequestParams())
        .then((resp) => {
          this.workbench = Object.assign({}, this.workbench, resp);
          this.$nextTick(this.renderCharts);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    buildRequestParams() {
      const params = {
        time_range: this.search.time_range,
      };
      if (
        this.search.time_range === "custom" &&
        this.search.custom_time &&
        this.search.custom_time.length === 2
      ) {
        params.start_time = Math.floor(
          Number(this.search.custom_time[0]) / 1000
        );
        params.end_time =
          Math.floor(Number(this.search.custom_time[1]) / 1000) + 86399;
      }
      return params;
    },
    chart(ref) {
      if (!this.$refs[ref]) return null;
      if (!this.charts[ref]) {
        this.charts[ref] = echarts.init(this.$refs[ref]);
      }
      return this.charts[ref];
    },
    renderCharts() {
      this.renderClueTrend();
      this.renderClueStatus();
      this.renderSiteTop();
      this.renderSeoGauge();
      this.renderGoogleTrend();
      this.resizeCharts();
    },
    renderClueTrend() {
      const chart = this.chart("clueTrendChart");
      if (!chart) return;
      const rows = this.workbench.clue_trend || [];
      chart.setOption({
        color: ["#1677ff", "#13c2c2"],
        tooltip: {
          trigger: "axis",
          confine: true,
          backgroundColor: "rgba(24, 38, 61, 0.92)",
          borderWidth: 0,
          textStyle: { color: "#fff" },
          axisPointer: {
            type: "line",
            lineStyle: { color: "#9bbcff", type: "dashed" },
          },
        },
        legend: {
          top: 0,
          right: 0,
          itemWidth: 18,
          itemHeight: 8,
          textStyle: { color: "#607089", fontSize: 12 },
        },
        grid: { top: 42, right: 18, bottom: 32, left: 42 },
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: rows.map((item) => item.label),
          axisTick: { show: false },
          axisLine: { lineStyle: { color: "#dfe6f0" } },
          axisLabel: { color: "#607089" },
        },
        yAxis: {
          type: "value",
          minInterval: 1,
          splitLine: { lineStyle: { color: "#edf1f7" } },
          axisLabel: { color: "#607089" },
        },
        series: [
          this.lineSeries(
            "线索总数",
            rows.map((item) => item.total),
            true,
            "#2f7cf6"
          ),
          this.lineSeries(
            "已转发",
            rows.map((item) => item.forwarded),
            false,
            "#18c4b8"
          ),
        ],
      });
    },
    renderClueStatus() {
      const chart = this.chart("clueStatusChart");
      if (!chart) return;
      const rows = this.workbench.clue_status || [];
      const total = rows.reduce(
        (sum, item) => sum + Number(item.value || 0),
        0
      );
      chart.setOption({
        color: ["#2f7cf6", "#18c48f", "#ff9f1a", "#ff4d57"],
        tooltip: { trigger: "item", confine: true },
        legend: {
          orient: "vertical",
          top: "middle",
          right: 18,
          itemWidth: 11,
          itemHeight: 11,
          icon: "circle",
          textStyle: { color: "#607089", fontSize: 12 },
          formatter: (name) => {
            const target = rows.find((item) => item.name === name);
            return `${name}  ${target ? target.value : 0}`;
          },
        },
        graphic: [
          {
            type: "text",
            left: "34%",
            top: "42%",
            style: {
              text: this.formatNumber(total),
              textAlign: "center",
              fill: "#18263d",
              fontSize: 28,
              fontWeight: 700,
            },
          },
          {
            type: "text",
            left: "34%",
            top: "53%",
            style: {
              text: "总线索",
              textAlign: "center",
              fill: "#8a97aa",
              fontSize: 13,
            },
          },
        ],
        series: [
          {
            name: "线索状态",
            type: "pie",
            radius: ["53%", "74%"],
            center: ["35%", "50%"],
            minAngle: 4,
            avoidLabelOverlap: true,
            label: { show: false },
            labelLine: { show: false },
            itemStyle: {
              borderColor: "#fff",
              borderWidth: 5,
              borderRadius: 8,
              shadowBlur: 14,
              shadowColor: "rgba(34, 50, 74, 0.12)",
            },
            emphasis: {
              scale: true,
              scaleSize: 6,
              itemStyle: {
                shadowBlur: 20,
                shadowColor: "rgba(34, 50, 74, 0.2)",
              },
            },
            data: rows,
          },
          {
            type: "pie",
            radius: ["81%", "82%"],
            center: ["35%", "50%"],
            silent: true,
            label: { show: false },
            data: [{ value: 1, itemStyle: { color: "#edf2f8" } }],
          },
        ],
      });
    },
    renderSiteTop() {
      const chart = this.chart("siteTopChart");
      if (!chart) return;
      const rows = (this.workbench.site_top || []).slice().reverse();
      const values = rows.map((item) => Number(item.clue_count || 0));
      const max = Math.max.apply(null, values) || 1;
      chart.setOption({
        color: ["#2f7cf6"],
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          confine: true,
          backgroundColor: "rgba(24, 38, 61, 0.92)",
          borderWidth: 0,
          textStyle: { color: "#fff" },
        },
        grid: { top: 8, right: 44, bottom: 20, left: 100 },
        xAxis: {
          type: "value",
          minInterval: 1,
          splitLine: { lineStyle: { color: "#edf1f7" } },
          axisLabel: { color: "#607089" },
        },
        yAxis: {
          type: "category",
          data: rows.map((item) => this.shortText(item.site_label || "-", 10)),
          axisTick: { show: false },
          axisLine: { lineStyle: { color: "#dfe6f0" } },
          axisLabel: { color: "#607089" },
        },
        series: [
          {
            name: "背景",
            type: "bar",
            barWidth: 14,
            barGap: "-100%",
            data: rows.map(() => max),
            silent: true,
            itemStyle: {
              color: "#edf3fb",
              borderRadius: [0, 8, 8, 0],
            },
          },
          {
            name: "线索数",
            type: "bar",
            barWidth: 14,
            data: values,
            label: {
              show: true,
              position: "right",
              color: "#31415a",
              fontWeight: 600,
            },
            itemStyle: {
              borderRadius: [0, 8, 8, 0],
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: "#12c2e9" },
                { offset: 1, color: "#2f7cf6" },
              ]),
              shadowBlur: 10,
              shadowColor: "rgba(47, 124, 246, 0.22)",
            },
          },
        ],
      });
    },
    renderSeoGauge() {
      const chart = this.chart("seoGaugeChart");
      if (!chart) return;
      const rate = Number(this.workbench.seo.achievement_rate || 0);
      chart.setOption({
        series: [
          {
            type: "gauge",
            min: 0,
            max: 100,
            radius: "94%",
            progress: {
              show: true,
              width: 14,
              roundCap: true,
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                  { offset: 0, color: "#1cd4a7" },
                  { offset: 1, color: "#2f7cf6" },
                ]),
                shadowBlur: 12,
                shadowColor: "rgba(47, 124, 246, 0.28)",
              },
            },
            axisLine: { lineStyle: { width: 14, color: [[1, "#e9eef5"]] } },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: { show: false },
            pointer: { show: false },
            anchor: { show: false },
            detail: {
              valueAnimation: true,
              formatter: "{value}%",
              color: "#22324a",
              fontSize: 28,
              offsetCenter: [0, "8%"],
            },
            title: {
              offsetCenter: [0, "45%"],
              color: "#7a8798",
              fontSize: 13,
            },
            data: [{ value: rate, name: "达标率" }],
          },
        ],
      });
    },
    renderGoogleTrend() {
      const chart = this.chart("googleTrendChart");
      if (!chart) return;
      const rows = (this.workbench.google && this.workbench.google.trend) || [];
      chart.setOption({
        color: ["#1677ff", "#f59a23"],
        tooltip: {
          trigger: "axis",
          confine: true,
          backgroundColor: "rgba(24, 38, 61, 0.92)",
          borderWidth: 0,
          textStyle: { color: "#fff" },
        },
        legend: {
          top: 0,
          right: 0,
          itemWidth: 18,
          itemHeight: 8,
          textStyle: { color: "#607089", fontSize: 12 },
        },
        grid: { top: 40, right: 18, bottom: 28, left: 42 },
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: rows.map((item) => item.month),
          axisTick: { show: false },
          axisLine: { lineStyle: { color: "#dfe6f0" } },
          axisLabel: { color: "#607089" },
        },
        yAxis: {
          type: "value",
          minInterval: 1,
          splitLine: { lineStyle: { color: "#edf1f7" } },
          axisLabel: { color: "#607089" },
        },
        series: [
          this.lineSeries(
            "GA4访问",
            rows.map((item) => item.sessions),
            true,
            "#7c5cff"
          ),
          this.lineSeries(
            "GSC点击",
            rows.map((item) => item.search_clicks),
            false,
            "#ff9f1a"
          ),
        ],
      });
    },
    lineSeries(name, data, area, color) {
      return {
        name,
        type: "line",
        smooth: true,
        symbol: "circle",
        symbolSize: 6,
        data: data.map((item) => Number(item || 0)),
        areaStyle: area
          ? {
              opacity: 1,
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: this.hexToRgba(color || "#1677ff", 0.24) },
                { offset: 1, color: this.hexToRgba(color || "#1677ff", 0.02) },
              ]),
            }
          : undefined,
        itemStyle: {
          color: color || "#1677ff",
          borderColor: "#fff",
          borderWidth: 2,
        },
        lineStyle: {
          width: 3,
          color: color || undefined,
          shadowBlur: 8,
          shadowColor: this.hexToRgba(color || "#1677ff", 0.28),
        },
      };
    },
    resizeCharts() {
      Object.keys(this.charts).forEach((key) => {
        this.charts[key].resize();
      });
    },
    formatNumber(value) {
      const number = Number(value || 0);
      return number.toLocaleString();
    },
    trendText(card) {
      return (card.trend && card.trend.text) || "暂无对比";
    },
    trendDirection(card) {
      return (card.trend && card.trend.direction) || "flat";
    },
    normalizedSparkline(values) {
      const list =
        Array.isArray(values) && values.length ? values : [0, 0, 0, 0, 0, 0, 0];
      const max =
        Math.max.apply(
          null,
          list.map((item) => Number(item || 0))
        ) || 1;
      return list.map((item) => Number(item || 0) / max);
    },
    sparklineLinePoints(values) {
      const list = this.normalizedSparkline(values);
      const step = list.length > 1 ? 96 / (list.length - 1) : 96;
      return list
        .map((item, index) => {
          const x = 2 + index * step;
          const y = 30 - item * 24;
          return `${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .join(" ");
    },
    sparklineAreaPoints(values) {
      const line = this.sparklineLinePoints(values);
      return `2,32 ${line} 98,32`;
    },
    metricIcon(key) {
      const icons = {
        range_clue: "el-icon-message",
        forwarded_clue: "el-icon-check",
        waiting_clue: "el-icon-time",
        failed_clue: "el-icon-warning-outline",
        range_site: "el-icon-monitor",
        sync_failed: "el-icon-warning-outline",
      };
      return icons[key] || "el-icon-data-analysis";
    },
    hexToRgba(hex, opacity) {
      const value = String(hex || "#1677ff").replace("#", "");
      const full =
        value.length === 3
          ? value
              .split("")
              .map((item) => item + item)
              .join("")
          : value;
      const r = parseInt(full.slice(0, 2), 16);
      const g = parseInt(full.slice(2, 4), 16);
      const b = parseInt(full.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    },
    shortText(text, length) {
      text = String(text || "");
      return text.length > length ? text.slice(0, length) + "..." : text;
    },
  },
};
</script>

<style lang="scss" scoped>
.workbench-dashboard {
  padding: 16px;
  background: radial-gradient(
      circle at 18% 0%,
      rgba(47, 124, 246, 0.08),
      transparent 32%
    ),
    radial-gradient(circle at 85% 8%, rgba(24, 196, 184, 0.08), transparent 28%),
    #f4f7fb;
  min-height: calc(100vh - 84px);
}

.dashboard-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
}

.dashboard-title {
  color: #22324a;
  font-size: 22px;
  font-weight: 600;
  line-height: 32px;
}

.dashboard-subtitle {
  color: #8a97aa;
  font-size: 13px;
  line-height: 22px;
}

.dashboard-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.time-range-tabs {
  display: inline-flex;

  ::v-deep .el-radio-button__inner {
    min-width: 72px;
    color: #5d6c82;
    border-color: #dfe6f0;
    background: #f8fbff;
  }

  ::v-deep .el-radio-button__orig-radio:checked + .el-radio-button__inner {
    color: #fff;
    border-color: #2f7cf6;
    background: linear-gradient(135deg, #2f7cf6, #12c2e9);
    box-shadow: -1px 0 0 0 #2f7cf6;
  }
}

.custom-range-picker {
  width: 260px;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
}

.metric-card {
  position: relative;
  overflow: hidden;
  background: #fff;
  border: 1px solid rgba(223, 230, 240, 0.92);
  border-radius: 8px;
  padding: 18px 18px 12px;
  min-width: 0;
  min-height: 156px;
  box-shadow: 0 10px 24px rgba(34, 50, 74, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 30px rgba(34, 50, 74, 0.1);
  }
}

.metric-card__glow {
  position: absolute;
  right: -38px;
  top: -38px;
  width: 110px;
  height: 110px;
  border-radius: 50%;
  opacity: 0.14;
  background: #2f7cf6;
  pointer-events: none;
}

.metric-card__top {
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  z-index: 1;
}

.metric-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 8px;
  color: #fff;
  background: linear-gradient(135deg, #2f7cf6, #12c2e9);
  box-shadow: 0 10px 18px rgba(47, 124, 246, 0.28);

  i {
    font-size: 21px;
  }
}

.metric-card__label {
  color: #5d6c82;
  font-size: 14px;
  font-weight: 600;
}

.metric-card__value {
  position: relative;
  z-index: 1;
  margin-top: 14px;
  color: #18263d;
  font-size: 32px;
  font-weight: 700;
  line-height: 40px;
  white-space: nowrap;

  small {
    margin-left: 5px;
    color: #8a97aa;
    font-size: 13px;
    font-weight: 400;
  }
}

.metric-card__bottom {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
  min-height: 24px;
}

.trend-badge {
  display: inline-flex;
  align-items: center;
  max-width: 130px;
  color: #8a97aa;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.trend-badge--up {
  color: #1ca66a;
}

.trend-badge--down {
  color: #e95b5b;
}

.sparkline {
  position: absolute;
  right: 12px;
  bottom: 8px;
  width: 92px;
  height: 36px;
  opacity: 0.95;
}

.sparkline__area {
  fill: rgba(47, 124, 246, 0.12);
}

.sparkline__line {
  fill: none;
  stroke: #2f7cf6;
  stroke-width: 2.6;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.metric-card--forwarded_clue .metric-card__icon,
.metric-card--forwarded_clue .metric-card__glow {
  background: linear-gradient(135deg, #13c2c2, #1cd4a7);
}

.metric-card--forwarded_clue .metric-card__icon {
  box-shadow: 0 10px 18px rgba(19, 194, 194, 0.28);
}

.metric-card--forwarded_clue .sparkline__line {
  stroke: #13c2c2;
}

.metric-card--forwarded_clue .sparkline__area {
  fill: rgba(19, 194, 194, 0.13);
}

.metric-card--waiting_clue .metric-card__icon,
.metric-card--waiting_clue .metric-card__glow {
  background: linear-gradient(135deg, #00a8ff, #00d2ff);
}

.metric-card--failed_clue .metric-card__icon,
.metric-card--failed_clue .metric-card__glow {
  background: linear-gradient(135deg, #ff9f1a, #ffc247);
}

.metric-card--failed_clue .metric-card__icon {
  box-shadow: 0 10px 18px rgba(255, 159, 26, 0.3);
}

.metric-card--failed_clue .sparkline__line {
  stroke: #ff9f1a;
}

.metric-card--failed_clue .sparkline__area {
  fill: rgba(255, 159, 26, 0.15);
}

.metric-card--range_site .metric-card__icon,
.metric-card--range_site .metric-card__glow {
  background: linear-gradient(135deg, #7c5cff, #a874ff);
}

.metric-card--range_site .metric-card__icon {
  box-shadow: 0 10px 18px rgba(124, 92, 255, 0.3);
}

.metric-card--range_site .sparkline__line {
  stroke: #7c5cff;
}

.metric-card--range_site .sparkline__area {
  fill: rgba(124, 92, 255, 0.14);
}

.metric-card--sync_failed .metric-card__icon,
.metric-card--sync_failed .metric-card__glow {
  background: linear-gradient(135deg, #ff4d57, #ff8a65);
}

.metric-card--sync_failed .metric-card__icon {
  box-shadow: 0 10px 18px rgba(255, 77, 87, 0.26);
}

.metric-card--sync_failed .sparkline__line {
  stroke: #ff4d57;
}

.metric-card--sync_failed .sparkline__area {
  fill: rgba(255, 77, 87, 0.12);
}

.dashboard-row {
  margin-top: 16px;
}

.chart-panel,
.health-panel {
  border-radius: 8px;
  border-color: #e6ebf2;
  box-shadow: 0 10px 24px rgba(34, 50, 74, 0.05);

  ::v-deep .el-card__header {
    background: linear-gradient(180deg, #fff, #fbfdff);
  }
}

.panel-title {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  color: #22324a;
  font-size: 15px;
  font-weight: 600;

  small {
    color: #8a97aa;
    font-size: 12px;
    font-weight: 400;
  }
}

.chart-canvas {
  width: 100%;
  height: 300px;
}

.chart-canvas--large {
  height: 330px;
}

.chart-canvas--small {
  height: 250px;
}

.seo-panel {
  display: flex;
  align-items: center;
  min-height: 300px;
}

.seo-panel__gauge {
  width: 42%;
  height: 260px;
}

.seo-panel__stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  flex: 1;

  div {
    padding: 16px;
    background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0.9),
        rgba(247, 250, 255, 0.96)
      ),
      #f7f9fc;
    border: 1px solid #edf1f7;
    border-radius: 8px;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85);
  }

  span {
    display: block;
    color: #7a8798;
    font-size: 13px;
  }

  strong {
    display: block;
    margin-top: 8px;
    color: #22324a;
    font-size: 24px;
    line-height: 30px;
  }
}

.health-list {
  min-height: 250px;
  padding: 2px 0;
}

.health-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  margin-bottom: 10px;
  background: #f8fbff;
  border: 1px solid #edf1f7;
  border-radius: 8px;

  &:last-child {
    margin-bottom: 0;
  }

  strong {
    display: block;
    color: #22324a;
    font-size: 14px;
    line-height: 20px;
  }

  small {
    display: block;
    margin-top: 2px;
    color: #8a97aa;
    font-size: 12px;
    line-height: 18px;
  }
}

.health-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #b8c2d0;
  flex: 0 0 auto;
}

.health-dot--success {
  background: #1ca66a;
}

.health-dot--warning,
.health-dot--pending {
  background: #f59a23;
}

.health-dot--danger {
  background: #e95b5b;
}

@media (max-width: 1400px) {
  .metric-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .workbench-dashboard {
    padding: 12px;
  }

  .metric-grid {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }

  .seo-panel {
    display: block;
  }

  .seo-panel__gauge {
    width: 100%;
  }
}
</style>
